import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { useMutation } from "@tanstack/react-query";
import {
  AllCommunityModule,
  ColDef,
  GetRowIdParams,
  ModuleRegistry,
  RowSelectionOptions,
  Theme,
  themeQuartz
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Stack, LinearProgress, Typography } from "@mui/material";
import { fetchGraphQL } from "../../../Graphql/utils";
import AlertBannerContext from "../../../Contexts/alertBannerContext";
import {
  GetServiceChatBookingResponse,
  ServiceChatBooking
} from "../../../Graphql/types/serviceChatBooking";
import { GetAllServiceChatBookingQuery } from "../../../Graphql/querries/serviceChatBooking";
import ViewBookingDetailButton from "./ViewBookingDetailButton";
import WithAuth from "../../../Hoc/WithAuth";

ModuleRegistry.registerModules([AllCommunityModule]);

const ChatBooking = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const [rowData, setRowData] = useState<ServiceChatBooking[]>([]);
  const [columnDefs, setColumnDefs] = useState<ColDef[]>();
  const containerStyle = useMemo(
    () => ({ width: "100%", height: "calc(100vh - 100px)" }),
    []
  );
  const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
  const myTheme = themeQuartz.withParams({
    // color and style of border around selection
    rangeSelectionBorderColor: "rgb(193, 0, 97)",
    rangeSelectionBorderStyle: "dashed",
    // background color of selection - you can use a semi-transparent color
    // and it wil overlay on top of the existing cells
    rangeSelectionBackgroundColor: "rgb(255, 0, 128, 0.1)",
    // color used to indicate that data has been copied form the cell range
    rangeSelectionHighlightColor: "rgb(60, 188, 0, 0.3)"
  });

  const theme = useMemo<Theme | "legacy">(() => {
    return myTheme;
  }, []);

  const { mutate, isPending } = useMutation<
    GetServiceChatBookingResponse,
    Error
  >({
    mutationKey: ["getChatBooking"],
    mutationFn: () =>
      fetchGraphQL(GetAllServiceChatBookingQuery, { input: {} }, setAlert),
    onSuccess: (response) => {
      setRowData(response.getAllServiceChatBooking || []);
    },
    onError: () => {
      setRowData([]);
    }
  });

  const rowSelection: RowSelectionOptions = {
    mode: "singleRow",
    checkboxes: false
  };

  useEffect(() => {
    mutate();
    setColumnDefs([
      { field: "firstName" },
      { field: "lastName" },
      { field: "email" },
      { field: "phoneNumber" },
      { field: "status" },
      { field: "createdAt" },
      {
        field: "actions",
        headerName: "Actions",
        cellRenderer: ViewBookingDetailButton
      }
    ]);
  }, []);

  const getRowId = useCallback(
    (params: GetRowIdParams) => String(params.data.id),
    []
  );

  const defaultColDef = useMemo(() => {
    return {
      filter: true,
      floatingFilter: true,
      flex: 1,
      minWidth: 100,
      resizable: true
    };
  }, []);

  return (
    <div style={containerStyle}>
      {isPending && (
        <Stack width="100%" paddingBottom="1em" alignSelf="center">
          <LinearProgress />
        </Stack>
      )}
      <Stack
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "row",
          width: "100%",
          padding: "1em"
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          Chat booking Listing
        </Typography>
      </Stack>
      <div style={gridStyle}>
        <AgGridReact
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowSelection={rowSelection}
          pagination
          paginationPageSize={10}
          paginationPageSizeSelector={[10, 25, 50]}
          theme={theme}
          getRowId={getRowId}
        />
      </div>
    </div>
  );
};

export default WithAuth(ChatBooking);
